import { useRef, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  ClipboardList, Building2, RefreshCw, FlaskConical,
  FileText, Database, Wind, Briefcase, Scale,
  Shield, CheckCircle2, GraduationCap, Cloud, Phone, ArrowRight,
  Award, ShieldCheck, FileCheck, Users, Clock, MapPin,
  Star, PhoneCall, Search, Microscope, FileBarChart, Quote,
  Home, HardHat
} from 'lucide-react';
import { servicesData } from '../data/services';
import { Button } from '../components/ui/button';

// Mobile detection and reduced motion preference
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile, { passive: true });
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return isMobile;
};

const usePrefersReducedMotion = () => {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mq.matches);
    const handler = (e) => setPrefersReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return prefersReduced;
};

const iconMap = {
  ClipboardList, Building2, RefreshCw, FlaskConical,
  FileText, Database, Wind, Briefcase, Scale,
  HardHat: Shield, CheckCircle2, GraduationCap, Cloud
};

const STATS = [
  { value: 15, suffix: '+', label: 'Years Experience' },
  { value: 5000, suffix: '+', label: 'Surveys Completed' },
  { value: 100, suffix: '%', label: 'Client Satisfaction' },
  { value: 24, suffix: 'hr', label: 'Response Time' },
];

/* ─── animation helpers (disabled on mobile for performance) ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUpMobile = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0, transition: { duration: 0 } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

const fadeInMobile = {
  hidden: { opacity: 1 },
  visible: { opacity: 1, transition: { duration: 0 } },
};

const slideRight = {
  hidden: { opacity: 0, x: -60 },
  visible: (i = 0) => ({
    opacity: 1, x: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const slideRightMobile = {
  hidden: { opacity: 1, x: 0 },
  visible: { opacity: 1, x: 0, transition: { duration: 0 } },
};

function useAnimatedCounter(end, duration = 2000, startCounting) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!startCounting) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration, startCounting]);
  return count;
}

const StatCard = ({ value, suffix, label, index, inView }) => {
  const count = useAnimatedCounter(value, 1800, inView);
  return (
    <motion.div
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      className="relative text-center px-6 py-5"
      data-testid={`stat-card-${index}`}
    >
      <div className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-white">
        {count}{suffix}
      </div>
      <div className="text-sm text-white/70 mt-1 uppercase tracking-widest font-medium">
        {label}
      </div>
    </motion.div>
  );
};

const AnimSection = ({ children, className, variants = fadeUp, mobileVariants, custom = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isMobile = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();
  
  // Use simplified animations on mobile or when user prefers reduced motion
  const activeVariants = (isMobile || prefersReducedMotion) 
    ? (mobileVariants || { hidden: { opacity: 1 }, visible: { opacity: 1 } })
    : variants;
  
  return (
    <motion.div
      ref={ref}
      custom={custom}
      variants={activeVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function HomePage() {
  const isMobile = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: '-50px' });
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  
  // Disable parallax on mobile for better performance
  const heroY = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], isMobile ? [1, 1] : [1, 0]);

  const featuredServices = servicesData.slice(0, 6);

  return (
    <>
      <SEO 
        title="Asbestosurv | Professional Asbestos Surveys Across the UK"
        description="Professional asbestos surveys across the UK. Management & refurbishment surveys — quotes tailored to your property. BOHS P402 / RSPH Level 3 qualified surveyors, independent UKAS accredited lab. Reports in 24-48 hours. Call 07704 885084."
        keywords="asbestos survey, asbestos management survey, asbestos refurbishment survey, asbestos survey near me, asbestos survey UK, asbestos survey Essex, asbestos survey London, asbestos survey Kent, asbestos surveyor, CAR 2012 compliance, duty to manage asbestos"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Asbestosurv",
          "description": "Professional asbestos surveys and management services across the UK. CAR 2012 compliant, HSG264 compliant.",
          "url": "https://asbestosurv.co.uk",
          "telephone": "07704 885084",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "26 White Horse Lane",
            "addressLocality": "Maldon",
            "addressRegion": "Essex",
            "postalCode": "CM9 5QP",
            "addressCountry": "GB"
          },
          "areaServed": "United Kingdom",
          "priceRange": "$$",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Asbestos Services",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Asbestos Management Surveys" }},
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Refurbishment & Demolition Surveys" }},
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Asbestos Air Monitoring" }},
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Bulk Sampling & Analysis" }}
            ]
          }
        }}
      />

      {/* ════════════════════ HERO ════════════════════ */}
      <section ref={heroRef} className="relative min-h-[100vh] flex items-center overflow-hidden bg-[#111]" data-testid="hero-section">
        {/* Parallax background image */}
        <motion.div
          style={{ y: heroY }}
          className="absolute inset-0 w-full h-[120%]"
        >
          <img
            src="https://images.unsplash.com/photo-1586947292720-16654d57e52e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxhc2Jlc3RvcyUyMHJlbW92YWwlMjB3b3JrZXIlMjB3aGl0ZSUyMGhhem1hdCUyMHN1aXQlMjBwcm90ZWN0aXZlJTIwbWFzayUyMGJ1aWxkaW5nJTIwaW5zcGVjdGlvbnxlbnwwfHx8fDE3NzQ0NDczNjl8MA&ixlib=rb-4.1.0&q=85&w=1920"
            srcSet="
              https://images.unsplash.com/photo-1586947292720-16654d57e52e?crop=entropy&cs=srgb&fm=jpg&w=640&q=75 640w,
              https://images.unsplash.com/photo-1586947292720-16654d57e52e?crop=entropy&cs=srgb&fm=jpg&w=1024&q=80 1024w,
              https://images.unsplash.com/photo-1586947292720-16654d57e52e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85 1920w
            "
            sizes="100vw"
            alt="Asbestos surveyor in protective equipment"
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111]/95 via-[#111]/70 to-[#111]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />
        </motion.div>

        {/* Hero content - LEFT ALIGNED */}
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-20 pb-32">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 text-primary px-4 py-2 text-sm font-semibold uppercase tracking-widest mb-8"
            >
              <ShieldCheck className="h-4 w-4" />
              CAR 2012 Fully Compliant
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl sm:text-5xl lg:text-6xl font-headings font-bold text-white mb-6 leading-[1.1]"
            >
              Asbestos Surveys
              <br />
              <span className="text-primary">Across the UK</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="text-lg md:text-xl text-white font-medium mb-4"
            >
              Reports Delivered in 24–48 Hours
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="space-y-2.5 mb-10"
            >
              {[
                'Management, Refurbishment & Demolition Surveys',
                'Independent UKAS accredited Laboratory Analysis',
                'BOHS P402 / RSPH Level 3 Qualified Surveyors',
                'Clear, Fixed Quotations — Domestic Management Surveys from £155',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-white/85">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm md:text-base">{item}</span>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button asChild size="lg" className="text-base px-8 py-6 shadow-lg shadow-primary/25" data-testid="hero-contact-button">
                <Link to="/contact">
                  Request a Quote
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="text-base px-8 py-6 bg-white text-secondary hover:bg-white/90 font-bold"
                data-testid="hero-call-button"
              >
                <a href="tel:07704885084">
                  <PhoneCall className="h-5 w-5 mr-2" />
                  Call 07704 885084
                </a>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="flex items-center gap-6 mt-10 text-sm text-white/60"
            >
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                Same-Day Booking Available
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Nationwide Coverage
              </span>
            </motion.div>
          </div>
        </motion.div>

        {/* Stats bar at bottom of hero */}
        <div ref={statsRef} className="absolute bottom-0 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/10 backdrop-blur-md bg-white/5" data-testid="stats-bar">
              {STATS.map((stat, i) => (
                <StatCard key={i} index={i} inView={statsInView} {...stat} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ SERVICES ════════════════════ */}
      <section id="services" className="py-24 md:py-36 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
            <AnimSection className="max-w-2xl">
              <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">What We Do</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-secondary leading-tight">
                Comprehensive Asbestos
                <br />Consultancy Services
              </h2>
            </AnimSection>
            <AnimSection custom={1}>
              <p className="text-base text-muted-foreground max-w-md leading-relaxed lg:text-right">
                From management surveys to air monitoring, we deliver expert solutions for full regulatory compliance.
              </p>
            </AnimSection>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
            {featuredServices.map((service, i) => {
              const Icon = iconMap[service.icon] || ClipboardList;
              return (
                <AnimSection key={service.slug} custom={i} className="bg-white">
                  <Link
                    to={`/services/${service.slug}`}
                    className="group block p-8 md:p-10 h-full hover:bg-slate-50/80 transition-all duration-500 relative overflow-hidden"
                    data-testid={`service-card-${service.slug}`}
                  >
                    <div className="absolute top-0 left-0 w-0 h-[3px] bg-primary group-hover:w-full transition-all duration-500" />
                    <Icon className="h-10 w-10 text-primary mb-6 group-hover:scale-110 transition-transform duration-500" />
                    <h3 className="text-lg font-bold text-secondary mb-3 font-headings leading-snug">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {service.shortDesc}
                    </p>
                    <span className="inline-flex items-center gap-2 text-primary text-sm font-semibold uppercase tracking-wide">
                      Learn More
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                    </span>
                  </Link>
                </AnimSection>
              );
            })}
          </div>

          <AnimSection className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="px-10" data-testid="view-all-services-btn">
              <Link to="/services/management-surveys">
                View All 13 Services
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════ HOW IT WORKS ════════════════════ */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimSection className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">Simple Process</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-secondary mb-4 leading-tight">
              How It Works
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              From initial enquiry to final report — we make the process fast, clear, and hassle-free.
            </p>
          </AnimSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', icon: PhoneCall, title: 'Enquiry', desc: 'Call us or request a quote online. We\'ll discuss your requirements and provide a clear quotation within hours.' },
              { step: '02', icon: Search, title: 'Site Visit', desc: 'Our BOHS P402 / RSPH Level 3 qualified surveyor attends your property at a time that suits you — including same-day bookings.' },
              { step: '03', icon: Microscope, title: 'Sampling & Analysis', desc: 'Samples are sent to an independent UKAS-accredited laboratory for definitive identification.' },
              { step: '04', icon: FileBarChart, title: 'Report Delivery', desc: 'Your comprehensive report is delivered within 24–48 hours, including risk assessments, photos, and recommendations.' },
            ].map((item, i) => (
              <AnimSection key={i} custom={i}>
                <div className="relative text-center group" data-testid={`process-step-${i}`}>
                  <div className="text-6xl font-headings font-bold text-primary/10 mb-4">{item.step}</div>
                  <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                    <item.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-bold text-secondary text-lg font-headings mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  {i < 3 && (
                    <div className="hidden lg:block absolute top-16 -right-4 w-8">
                      <ArrowRight className="h-5 w-5 text-primary/30" />
                    </div>
                  )}
                </div>
              </AnimSection>
            ))}
          </div>

          <AnimSection className="text-center mt-14">
            <Button asChild size="lg" className="text-base px-10 py-6 shadow-lg shadow-primary/25" data-testid="process-cta-btn">
              <Link to="/contact">
                Get Your Free Quote Today
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════ PRICING ════════════════════ */}
      <section className="py-24 md:py-32 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimSection className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">Transparent Pricing</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-secondary mb-4 leading-tight">
              Clear, Upfront Quotations
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              No hidden fees, no surprises. Tailored quotes based on your specific property and requirements.
            </p>
            <p className="text-xs text-muted-foreground mt-3 italic">
              Prices are quoted according to property size, survey scope, access requirements and anticipated sampling.
            </p>
          </AnimSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { type: 'Domestic', survey: 'Management Survey', price: '155', desc: 'Standard non-invasive survey for residential properties — identifying and assessing ACMs.', features: ['HSG264 Compliant', 'UKAS Lab Analysis', 'Report in 24–48hrs'] },
              { type: 'Domestic', survey: 'Refurbishment Survey', price: '195', desc: 'Invasive survey required before any refurbishment or demolition work in residential properties.', features: ['Pre-refurb Requirement', 'Destructive Inspection', 'Report in 24–48hrs'], popular: true },
              { type: 'Commercial', survey: 'Management Survey', price: '195', desc: 'Regulation 4 compliant management survey for non-domestic commercial premises.', features: ['Duty to Manage', 'Risk Assessments', 'Asbestos Register'] },
              { type: 'Commercial', survey: 'Refurbishment Survey', price: '255', desc: 'Comprehensive invasive survey for commercial refurbishment or demolition projects.', features: ['CAR 2012 Compliant', 'Full Scope R&D', 'Priority Turnaround'], popular: false },
            ].map((item, i) => (
              <AnimSection key={i} custom={i}>
                <div className={`relative h-full flex flex-col p-8 border ${item.popular ? 'border-primary ring-2 ring-primary/20' : 'border-border'} bg-white hover:shadow-lg transition-shadow duration-300`} data-testid={`pricing-card-${i}`}>
                  {item.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold uppercase tracking-wider px-4 py-1">
                      Most Popular
                    </div>
                  )}
                  <div className="mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">{item.type}</span>
                  </div>
                  <h3 className="font-bold text-secondary text-lg font-headings mb-4">{item.survey}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-sm text-muted-foreground">from</span>
                    <span className="text-4xl font-headings font-bold text-secondary">£{item.price}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-grow">{item.desc}</p>
                  <div className="space-y-2.5 mb-8">
                    {item.features.map((f, j) => (
                      <div key={j} className="flex items-center gap-2 text-sm text-secondary">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                        {f}
                      </div>
                    ))}
                  </div>
                  <Button asChild variant={item.popular ? 'default' : 'outline'} className="w-full" data-testid={`pricing-cta-${i}`}>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </div>
              </AnimSection>
            ))}
          </div>

          <AnimSection className="text-center mt-10">
            <p className="text-sm text-muted-foreground">
              Prices are guide rates. Final pricing depends on property size and scope.{' '}
              <a href="tel:07704885084" className="text-primary font-semibold hover:underline">Call 07704 885084</a> for an instant quote.
            </p>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════ TESTIMONIALS ════════════════════ */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimSection className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">Client Feedback</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-secondary mb-4 leading-tight">
              Trusted by Property Managers &<br />Contractors Across the UK
            </h2>
          </AnimSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Dean Burton',
                title: 'Benchmark Analyst',
                quote: 'Asbestosurv have been an invaluable partner for our benchmarking programme. Their reports are consistently thorough, accurately detailed, and delivered on time — exactly what we need for compliance reporting across a large property portfolio. Highly recommended.',
              },
              {
                name: 'Kim Cartwright AISEP MIWFM',
                title: 'Head of Compliance',
                quote: 'We rely on Asbestosurv for all of our asbestos survey requirements. Their understanding of Regulation 4 and the Duty to Manage is second to none. The quality of their risk assessments and asbestos registers has been instrumental in keeping our compliance programme on track.',
              },
              {
                name: 'Steph Taylor',
                title: 'PPM Transformation Manager',
                quote: 'Fast, professional, and genuinely easy to work with. Asbestosurv fitted seamlessly into our planned maintenance workflows — same-day booking, 24-hour reports, and clear recommendations that our contractors can act on immediately. They\'ve transformed how we manage asbestos across our sites.',
              },
            ].map((item, i) => (
              <AnimSection key={i} custom={i}>
                <div className="h-full flex flex-col p-8 bg-white border border-border hover:border-primary/20 transition-colors duration-300" data-testid={`testimonial-${i}`}>
                  <div className="flex gap-1 mb-5">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <Quote className="h-8 w-8 text-primary/20 mb-4" />
                  <p className="text-sm text-muted-foreground leading-relaxed flex-grow mb-6">
                    {item.quote}
                  </p>
                  <div className="border-t border-border pt-5">
                    <p className="font-bold text-secondary text-sm">{item.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.title}</p>
                  </div>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ WHO WE SERVE ════════════════════ */}
      <section className="py-24 md:py-32 bg-white" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimSection className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">Who We Serve</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-secondary mb-4 leading-tight">
              Expert Solutions for Every Sector
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Whether you manage a property portfolio, plan a refurbishment, or are buying a home — we have a survey tailored to your needs.
            </p>
          </AnimSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Building2,
                title: 'For Property & Facilities Managers',
                points: [
                  'Regulation 4 Duty to Manage compliance',
                  'Asbestos registers for your full portfolio',
                  'Reinspection programmes and monitoring',
                  'Priority risk assessments and action plans',
                  'Bulk pricing for multi-site portfolios',
                ],
                cta: 'Get a Portfolio Quote',
              },
              {
                icon: HardHat,
                title: 'For Refurbishment & Demolition Projects',
                points: [
                  'R&D surveys before any building work',
                  'CDM Regulation compliant reports',
                  'Contractor-ready removal specifications',
                  'Emergency and fast-track surveys',
                  'Four-stage clearance air testing',
                ],
                cta: 'Book a Pre-Works Survey',
              },
              {
                icon: Home,
                title: 'For Homebuyers & Homeowners',
                points: [
                  'Pre-purchase asbestos checks',
                  'Domestic management surveys — prices from £155*',
                  'Clear, jargon-free reports',
                  'Advice on safe management or removal',
                  'Fast turnaround — reports in 24–48 hours',
                ],
                cta: 'Get a Domestic Quote',
                note: '*Prices quoted based on property size and requirements',
              },
            ].map((item, i) => (
              <AnimSection key={i} custom={i}>
                <div className="h-full flex flex-col p-8 border border-border bg-white hover:shadow-lg transition-shadow duration-300" data-testid={`audience-card-${i}`}>
                  <div className="w-14 h-14 bg-primary/10 flex items-center justify-center mb-6">
                    <item.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-bold text-secondary text-lg font-headings mb-5">{item.title}</h3>
                  <div className="space-y-3 flex-grow mb-8">
                    {item.points.map((point, j) => (
                      <div key={j} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                  <Button asChild className="w-full" data-testid={`audience-cta-${i}`}>
                    <Link to="/contact">{item.cta}</Link>
                  </Button>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ FULL IMAGE BREAK ════════════════════ */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden" data-testid="image-break-section">
        <img
          src="https://images.pexels.com/photos/940289/pexels-photo-940289.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920"
          srcSet="
            https://images.pexels.com/photos/940289/pexels-photo-940289.jpeg?auto=compress&cs=tinysrgb&w=640 640w,
            https://images.pexels.com/photos/940289/pexels-photo-940289.jpeg?auto=compress&cs=tinysrgb&w=1024 1024w,
            https://images.pexels.com/photos/940289/pexels-photo-940289.jpeg?auto=compress&cs=tinysrgb&w=1920 1920w
          "
          sizes="100vw"
          alt="Modern commercial building"
          className="w-full h-full object-cover"
          loading="lazy"
          width="1920"
          height="1200"
        />
        <div className="absolute inset-0 bg-[#111]/60" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
            <AnimSection variants={slideRight} className="max-w-xl">
              <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-4">Duty to Manage</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-white leading-tight mb-6">
                Protecting People
                <br />& Property
              </h2>
              <p className="text-base text-white/80 leading-relaxed mb-8 max-w-lg">
                Under Regulation 4 of CAR 2012, dutyholders of non-domestic premises are
                legally required to manage asbestos risks. Our surveys provide the foundation
                for full compliance.
              </p>
              <Button asChild size="lg" data-testid="image-break-cta">
                <Link to="/about-asbestos">
                  Learn About Asbestos
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
              </Button>
            </AnimSection>
          </div>
        </div>
      </section>

      {/* ════════════════════ REGULATORY COMPLIANCE ════════════════════ */}
      <section className="py-24 md:py-36 bg-slate-50" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left - Images */}
            <div className="lg:col-span-5 space-y-6">
              <AnimSection>
                <div className="relative overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/4033019/pexels-photo-4033019.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200"
                    alt="UKAS Laboratory Analysis"
                    className="w-full h-72 md:h-80 object-cover"
                    loading="lazy"
                    width="1200"
                    height="800"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
                    <span className="text-white text-sm font-semibold">UKAS accredited Laboratory Analysis</span>
                  </div>
                </div>
              </AnimSection>
              <AnimSection custom={1}>
                <div className="relative overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/5802821/pexels-photo-5802821.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200"
                    alt="Professional surveyor on site"
                    className="w-full h-56 md:h-64 object-cover"
                    loading="lazy"
                    width="1200"
                    height="800"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
                    <span className="text-white text-sm font-semibold">BOHS P402 / RSPH Level 3 Surveyors</span>
                  </div>
                </div>
              </AnimSection>
            </div>

            {/* Right - Content */}
            <div className="lg:col-span-7">
              <AnimSection>
                <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">Standards & Compliance</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-secondary mb-6 leading-tight">
                  Regulatory Compliance &<br />Professional Standards
                </h2>
                <p className="text-base text-muted-foreground mb-10 leading-relaxed max-w-xl">
                  We maintain the highest standards of compliance with all current UK
                  asbestos regulations and guidance documentation.
                </p>
              </AnimSection>

              <div className="space-y-4">
                {[
                  {
                    title: 'Control of Asbestos Regulations 2012',
                    desc: 'All surveys comply with Regulation 4 (Duty to Manage), helping dutyholders fulfil legal obligations for identifying and managing ACMs in non-domestic premises.',
                    icon: ShieldCheck,
                  },
                  {
                    title: 'HSG264 Survey Methodology',
                    desc: 'Management and R&D surveys strictly follow HSG264 guidance through systematic visual inspection, minor intrusion, and destructive investigation where required.',
                    icon: FileCheck,
                  },
                  {
                    title: 'UKAS accredited Analysis',
                    desc: 'All samples analyzed by UKAS-accredited laboratories using polarised light microscopy for definitive identification and classification.',
                    icon: Award,
                  },
                  {
                    title: 'BOHS P402 / RSPH Level 3 Team',
                    desc: 'Surveyors hold BOHS P402 or RSPH Level 3 qualifications with ongoing CPD ensuring expertise in current legislation and best practice.',
                    icon: GraduationCap,
                  },
                ].map((item, i) => (
                  <AnimSection key={i} custom={i}>
                    <div className="flex gap-5 p-5 bg-white border border-border hover:border-primary/30 transition-colors duration-300 group">
                      <item.icon className="h-6 w-6 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="font-bold text-secondary mb-1.5 text-sm">{item.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </AnimSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ WHY CHOOSE US ════════════════════ */}
      <section className="py-24 md:py-36 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimSection className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">Why Choose Us</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-secondary mb-4 leading-tight">
              Why Choose Asbestosurv?
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Extensive experience and unwavering commitment to regulatory compliance,
              delivering asbestos management solutions that prioritize safety, accuracy,
              and legal adherence.
            </p>
          </AnimSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: 'Fully Compliant Surveys',
                desc: 'Every survey conducted in strict accordance with HSG264 methodology and CAR 2012 requirements.',
              },
              {
                icon: Award,
                title: 'UKAS accredited Analysis',
                desc: 'Material samples undergo analysis at UKAS-accredited laboratories providing legally defensible certification.',
              },
              {
                icon: GraduationCap,
                title: 'Qualified Surveyors',
                desc: 'BOHS P402 / RSPH Level 3 qualified with ongoing CPD ensuring expertise in current legislation and risk assessment.',
              },
              {
                icon: FileCheck,
                title: 'Comprehensive Reporting',
                desc: 'Detailed reports with annotated site plans, risk assessments, photographic evidence, and lab certificates.',
              },
              {
                icon: Wind,
                title: 'Air Monitoring (HSG248)',
                desc: 'Four-stage clearance certification with background monitoring, leak testing, and Certificates of Reoccupation.',
              },
              {
                icon: Clock,
                title: 'Rapid Turnaround',
                desc: 'Reports typically delivered within 5-7 working days. Expedited services available for urgent projects.',
              },
            ].map((item, i) => (
              <AnimSection key={i} custom={i}>
                <div className="group relative p-8 h-full border border-border bg-white hover:bg-slate-50 transition-all duration-300 overflow-hidden" data-testid={`why-card-${i}`}>
                  <div className="absolute bottom-0 left-0 w-full h-0 bg-primary/5 group-hover:h-full transition-all duration-500 ease-out" />
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                      <item.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-bold text-secondary text-lg font-headings mb-3">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ 2026 UPDATE BANNER ════════════════════ */}
      <section className="py-16 bg-secondary" data-testid="update-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <AnimSection className="lg:col-span-5" variants={slideRight}>
              <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3">Regulatory Update</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-headings font-bold text-white leading-tight">
                2026 HSE Consultation:<br />We're Prepared
              </h2>
            </AnimSection>
            <AnimSection className="lg:col-span-7" custom={1}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  'Analytical independence between contractors and labs',
                  'Strengthened surveyor competency verification',
                  'Clarified definitions for NNLW categories',
                  'Enhanced pre-renovation survey documentation',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 text-white/80">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </AnimSection>
          </div>
        </div>
      </section>

      {/* ════════════════════ CTA ════════════════════ */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="cta-section">
        <img
          src="https://images.pexels.com/photos/4981787/pexels-photo-4981787.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1920"
          srcSet="
            https://images.pexels.com/photos/4981787/pexels-photo-4981787.jpeg?auto=compress&cs=tinysrgb&w=640 640w,
            https://images.pexels.com/photos/4981787/pexels-photo-4981787.jpeg?auto=compress&cs=tinysrgb&w=1024 1024w,
            https://images.pexels.com/photos/4981787/pexels-photo-4981787.jpeg?auto=compress&cs=tinysrgb&w=1920 1920w
          "
          sizes="100vw"
          alt="Professional asbestos surveyor at work"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          width="1920"
          height="800"
        />
        <div className="absolute inset-0 bg-[#111]/80" />
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6 md:px-12">
          <AnimSection>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headings font-bold text-white mb-6 leading-tight">
              Book Your Survey Today
            </h2>
            <p className="text-base md:text-lg text-white/80 mb-4 leading-relaxed max-w-2xl mx-auto">
              Same-day booking available. Speak directly with a BOHS P402 / RSPH Level 3 qualified surveyor
              and receive your report within 24–48 hours.
            </p>
          </AnimSection>
          <AnimSection custom={1} className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button asChild size="lg" className="text-base px-8 py-6 shadow-lg shadow-primary/25" data-testid="cta-phone-button">
              <a href="tel:07704885084">
                <PhoneCall className="h-5 w-5 mr-2" />
                Call 07704 885084
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="text-base px-8 py-6 border-white/30 text-white hover:bg-white hover:text-secondary bg-transparent"
              data-testid="cta-contact-button"
            >
              <Link to="/contact">
                Request a Free Quote
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </AnimSection>
        </div>
      </section>
    </>
  );
}
